import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Column } from '../../types/Spreadsheet';
import { RootState } from '../store';

const columnsAdapter = createEntityAdapter({
  selectId: ( column: Column ) => column.id
  // sortComparer: ( a, b ) => a.label.localeCompare( b.label ) 
});

// ver
const initialState = columnsAdapter.getInitialState();

const columnsSlice = createSlice({
  name: 'columns',
  initialState,
  reducers: {
    setColumns: ( state, action: PayloadAction<Column[]> ) => {
      // Preserve hidden state from existing columns when backend sends fresh data
      // This prevents view hideColumns settings from being lost when data updates
      const newColumns = action.payload.map(( newCol ) => {
        const existingCol = state.entities[newCol.id];
        if ( existingCol && existingCol.hidden ) {
          return { ...newCol, hidden: true };
        }
        return newCol;
      });
      columnsAdapter.setAll( state, newColumns );
    },
    addColumn: columnsAdapter.addOne,
    addColumns: columnsAdapter.addMany,
    updateColumn: columnsAdapter.updateOne,
    removeColumn: columnsAdapter.removeOne,
    updateColumns: columnsAdapter.updateMany,
    removeColumns: columnsAdapter.removeMany,
    upsertColumn: columnsAdapter.upsertOne,
    upsertColumns: columnsAdapter.upsertMany,
    removeAllColumns: columnsAdapter.removeAll,
    hideColumns: ( state, action: PayloadAction<string | string[]> ) => {
      const ids = Array.isArray( action.payload ) ? action.payload : [action.payload];
      const updates = ids.map(( id ) => ({ id, changes: { hidden: true }}));
      columnsAdapter.updateMany( state, updates );
    },
    showAllColumns: ( state ) => {
      columnsAdapter.updateMany( state, state.ids.map(( id ) => ({ id, changes: { hidden: false }})));
    },
    setImageColumnWidth: ( state, action: PayloadAction<number> ) => {
      const newWidth = action.payload;
      const updates = state.ids
        .map(( id ) => state.entities[id]! )
        .filter(( col ) => col.type === 'image' || col.type === 'video' )
        .map(( col ) => ({
          id: col.id,
          changes: { width: newWidth }
        }));
      columnsAdapter.updateMany( state, updates );
    }
  }
});

export const {
  setColumns,
  addColumn,
  addColumns,
  updateColumn,
  removeColumn,
  updateColumns,
  removeColumns,
  upsertColumn,
  upsertColumns,
  removeAllColumns,
  hideColumns,
  showAllColumns,
  setImageColumnWidth
} = columnsSlice.actions;

export default columnsSlice.reducer;

export const {
  selectAll: selectAllColumns,
  selectById: selectColumnById,
  selectIds: selectColumnIds
} = columnsAdapter.getSelectors<RootState>(( state ) => state.spreadsheet.columns );